import { FC, useEffect, useState } from 'react';

import { Trans } from '@lingui/macro';
import { Box, Link, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import SessionManager from 'src/login/sessionManager';
import { jwtPayload } from 'src/utils/login';
import { TENANT_MIGRATED_EVENT, getGen2LoginUrl } from 'src/utils/tenantMigration';

// Navbar's own height (components/navbar/Navbar.tsx Root) — the overlay starts
// below it so the navbar stays reachable.
const NAVBAR_HEIGHT = 64;

const Overlay = styled(Box)({
    position: 'fixed',
    top: NAVBAR_HEIGHT,
    left: 0,
    right: 0,
    bottom: 0,
    // Navbar's Root has zIndex 1201; staying under it keeps the navbar clickable.
    zIndex: 1200,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const gen2LinkLabel = (url: string): string => {
    try {
        return new URL(url).host;
    } catch (e) {
        return url;
    }
};

const isAgentSession = (): boolean => !!jwtPayload(SessionManager.getAccessToken())?.agent;

const MigratedTenantModal: FC = () => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const show = () => {
            if (!isAgentSession()) {
                setIsOpen(true);
            }
        };
        window.addEventListener(TENANT_MIGRATED_EVENT, show);
        return () => window.removeEventListener(TENANT_MIGRATED_EVENT, show);
    }, []);

    if (!isOpen) {
        return null;
    }

    const gen2LoginUrl = getGen2LoginUrl();

    return (
        // Blocks the sidebar and page content so the user can't keep working in the
        // migrated tenant, but leaves the navbar reachable above it — that's where
        // the workspace switcher lives, so a user can still move to another tenant.
        <Overlay data-testid="migratedTenantModal">
            <Paper sx={{ maxWidth: 480, p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    <Trans>Migrated Workspace</Trans>
                </Typography>
                <Typography>
                    <Trans>
                        This apadua legacy workspace has been fully migrated to the Gen2
                        application. The legacy platform will soon be completely disabled.
                    </Trans>
                </Typography>
                {gen2LoginUrl && (
                    <Typography sx={{ mt: 2 }}>
                        <Trans>
                            Please use the following link to access the Gen2 apadua platform:
                        </Trans>{' '}
                        <Link href={gen2LoginUrl} data-testid="migratedTenantModalLink">
                            {gen2LinkLabel(gen2LoginUrl)}
                        </Link>
                    </Typography>
                )}
            </Paper>
        </Overlay>
    );
};

export default MigratedTenantModal;
